/* eslint-disable no-use-before-define */
import {
  Content,
  ContentTypeKeys,
  CurationContentTreatmentValues,
  CurationOpinionTreatmentValues,
  TreatmentType
} from 'components/context-menu-actions/mobile-card-options/types';
import {
  ArticleItem,
  ContentUnion,
  GraphicLinkedItem,
  GroupConfig,
  Maybe,
  MobileArticleAttributes,
  MobileArticleItem,
  MobileLinkedItem,
  ModuleContainer,
  ModuleContainerUnion,
  MultiZoneModuleContainer,
  PageContainer,
  SectionContainer,
  UiTitleTreatmentType
} from 'data/generated/graphql';
import { GRAPHIC_LINKED_ITEM, MOBILE_ARTICLE, MOBILE_LINKED_ITEM, MULTIZONE_MODULE } from './constants';
import { getCurationContentType } from './contentType';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';
import type { Container, EntityComponent, Metadata } from 'contexts/datamodel/DataModelContext.core';

interface MakeMobileArticleArgs {
  article: ArticleItem | MobileArticleItem;
  zone: number;
  rulesConfig: Record<string, unknown> | null | undefined;
}

export function isMobileLinkedItem(item: EntityComponent): item is MobileLinkedItem {
  return item.type === EntityType.LinkedItem && item.subtype === MOBILE_LINKED_ITEM;
}

export function isGraphicLinkedItem(item: EntityComponent): item is GraphicLinkedItem {
  return item.type === EntityType.LinkedItem && item.subtype === GRAPHIC_LINKED_ITEM;
}

export function makeMobileArticle({ article, zone, rulesConfig }: MakeMobileArticleArgs) {
  if (!rulesConfig) {
    throw new Error(
      `An unexpected error occurred: rulesConfig is of type ${typeof rulesConfig} with value ${rulesConfig}`
    );
  }

  const contentTypeMetadata = getCurationContentType(article);

  if (!contentTypeMetadata) {
    throw new Error(
      `An unexpected error occurred: contentTypeMetadata is of type ${typeof contentTypeMetadata} with value ${contentTypeMetadata}`
    );
  }

  const config = rulesConfig as unknown as Content;

  const zoneValues = ['L', 'M', 'S'];

  const mobileArticle: MobileArticleItem = {
    ...(article as MobileArticleItem),
    subtype: MOBILE_ARTICLE,
    attributes: {
      ...(article.attributes as MobileArticleAttributes)
    }
  };

  // ie: 'STANDARD_NEWS, 'BREAKING_NEWS' etc
  let curationContentTypeValue =
    CurationContentTreatmentValues[contentTypeMetadata.contentTreatment as keyof typeof CurationContentTreatmentValues];

  // If the content type is undefined, fallback to standard treatment
  if (
    curationContentTypeValue !== CurationContentTreatmentValues.standard &&
    curationContentTypeValue !== CurationContentTreatmentValues.breaking
  ) {
    curationContentTypeValue = CurationContentTreatmentValues.standard;
  }

  // If story type is opinion, use the opinion treatment values
  if (contentTypeMetadata.storyType === 'opinion') {
    curationContentTypeValue =
      CurationOpinionTreatmentValues[
        contentTypeMetadata.contentTreatment as keyof typeof CurationOpinionTreatmentValues
      ];
  }

  const storyType = config[contentTypeMetadata.storyType as keyof Content];

  const contentTypeConfig =
    storyType[
      contentTypeMapping[contentTypeMetadata.contentType as keyof typeof contentTypeMapping] as keyof typeof storyType
    ];

  const sizeConfig = contentTypeConfig ? contentTypeConfig[zoneValues[zone] as ContentTypeKeys] : undefined;

  if (!sizeConfig) {
    throw new Error(
      `Content type ${contentTypeMetadata.storyType} ${contentTypeMetadata.contentType} is not allowed in the zone ${zoneValues[zone]} `
    );
  }

  mobileArticle.attributes.size = zoneValues[zone] as ContentTypeKeys;

  const treatmentConfig = sizeConfig[curationContentTypeValue as keyof typeof sizeConfig];

  if (!treatmentConfig) {
    throw new Error(
      `Content type ${contentTypeMetadata.contentType} with treatment ${curationContentTypeValue} is not allowed in the zone ${zoneValues[zone]}`
    );
  }

  mobileArticle.attributes.treatment = curationContentTypeValue;

  let options = treatmentConfig.options ?? [];

  const filteredOptions = ['visual', 'showFlashline', 'centerText', 'redFlashline'];

  options = options.filter((option) => !filteredOptions.includes(Object.keys(option)[0]));

  // TODO: revisit this
  const visualOption = treatmentConfig.options?.find((option) => Object.keys(option)[0] === 'visual');
  if (!visualOption) {
    throw new Error(
      `Content type ${contentTypeMetadata.contentType} with treatment ${curationContentTypeValue} is missing visual option in zone ${zoneValues[zone]}`
    );
  }
  // Reset to default if the field is disabled in the new size, or if not yet set
  if (visualOption.visual?.disabled || typeof mobileArticle.attributes.visual !== 'string') {
    mobileArticle.attributes.visual = visualOption.visual?.defaultValue as string;
  }

  const flashline = treatmentConfig.options?.find((option) => Object.keys(option)[0] === 'showFlashline');

  if (!flashline) {
    throw new Error(
      `Content type ${contentTypeMetadata.contentType} with treatment ${curationContentTypeValue} is missing showFlashline option in zone ${zoneValues[zone]}`
    );
  }

  // Reset to default if the field is disabled in the new size, or if not yet set
  if (flashline.showFlashline?.disabled || typeof mobileArticle.attributes.showFlashline !== 'boolean') {
    mobileArticle.attributes.showFlashline = flashline.showFlashline?.defaultValue as boolean;
  }

  // centerText and redFlashline are only for L and M zones, and only for articles and live-coverage
  const isLargeOrMediumZone = zoneValues[zone] === 'L' || zoneValues[zone] === 'M';
  const isArticleOrLiveCoverage =
    contentTypeMetadata.contentType === 'article' || contentTypeMetadata.contentType === 'live-coverage';

  if (isLargeOrMediumZone && isArticleOrLiveCoverage) {
    const centerText = treatmentConfig.options?.find((option) => Object.keys(option)[0] === 'centerText');

    if (centerText) {
      // Reset to default if the field is disabled in the new size, or if not yet set
      if (centerText.centerText?.disabled || typeof mobileArticle.attributes.centerText !== 'boolean') {
        mobileArticle.attributes.centerText = centerText.centerText?.defaultValue as boolean;
      }
    }

    const redFlashline = treatmentConfig.options?.find((option) => Object.keys(option)[0] === 'redFlashline');

    if (redFlashline) {
      // Reset to default if the field is disabled in the new size, or if not yet set
      if (redFlashline.redFlashline?.disabled || typeof mobileArticle.attributes.redFlashline !== 'boolean') {
        mobileArticle.attributes.redFlashline = redFlashline.redFlashline?.defaultValue as boolean;
      }
    }
  } else {
    // Clear these fields if moving to a zone/content type that doesn't support them
    delete mobileArticle.attributes.centerText;
    delete mobileArticle.attributes.redFlashline;
  }

  options.forEach((option) => {
    const key = Object.keys(option)[0];

    const value = option[key]?.defaultValue;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (value !== undefined && value !== null) {
      // @ts-expect-error - TS doesn't like this, but it's fine
      mobileArticle.attributes[key as keyof MobileArticleAttributes] = value;
    }
  });

  return mobileArticle;
}

interface GetTreatmentArgs {
  item: MobileArticleItem | MobileLinkedItem;
}

export function getStoryType({ item }: GetTreatmentArgs) {
  const curationContentType = getCurationContentType(item);

  if (!curationContentType) {
    return 'news' as keyof Content;
  }

  return curationContentType.storyType as keyof Content;
}

interface GetContentTypeArgs {
  item: MobileArticleItem | MobileLinkedItem;
}

const contentTypeMapping = {
  article: 'article',
  video: 'video',
  audio: 'audio',
  'live-coverage': 'LC',
  'live-coverage-card': 'LCC',
  'linked-item': 'linkedItem'
};

export function getContentType({ item }: GetContentTypeArgs) {
  const curationContentType = getCurationContentType(item);

  if (!curationContentType) {
    // Fallback to article if content is not a string
    return 'article' as keyof TreatmentType;
  }

  return contentTypeMapping[curationContentType.contentType as keyof typeof contentTypeMapping] as keyof TreatmentType;
}

interface IsItemAllowedInZoneArgs {
  item: MobileArticleItem | MobileLinkedItem;
  rulesConfig: Record<string, unknown> | null | undefined;
}

export function isItemAllowedInZone({ item, rulesConfig }: IsItemAllowedInZoneArgs) {
  if (!rulesConfig) {
    return false;
  }

  const GridOConfig = rulesConfig as unknown as Content;

  const treatment = getStoryType({ item });
  const contentType = getContentType({ item });

  const sizeConfigs = GridOConfig[treatment][contentType];

  const itemSizeConfigs = sizeConfigs ? sizeConfigs[item.attributes.size as ContentTypeKeys] : undefined;

  return !!itemSizeConfigs;
}

const contentTypesReadable = {
  article: 'Article',
  video: 'Video',
  audio: 'Audio',
  LC: 'Live coverage',
  LCC: 'Live coverage card',
  linkedItem: 'Linked Item'
};

const sizeReadable = {
  L: 'large',
  M: 'medium',
  S: 'small'
};

export const validateIndividualModule = (
  item: ContentUnion | PageContainer | ModuleContainerUnion,
  rulesConfig: Record<string, unknown> | null | undefined
) => {
  if (item.type === 'Module' && item.subtype === MULTIZONE_MODULE) {
    const multizoneModule = item as MultiZoneModuleContainer;

    return multizoneModule.collection.map((collectionItem) => {
      if (isGraphicLinkedItem(collectionItem)) {
        // Check: does GraphicLinkedItem need rulesConfig validation?
        return true;
      }

      const allowed = isItemAllowedInZone({
        rulesConfig,
        item: collectionItem as MobileArticleItem | MobileLinkedItem
      });

      if (allowed) {
        return allowed;
      }

      const item = collectionItem as MobileArticleItem | MobileLinkedItem;

      const contentType = getContentType({ item });

      return {
        contentType: contentTypesReadable[contentType as keyof typeof contentTypesReadable],
        zone: sizeReadable[item.attributes.size as keyof typeof sizeReadable]
      };
    });
  }

  return [];
};

export const validateMultizoneModules = (
  pageDTO: { root: Container; metadata: Metadata },
  rulesConfig: Record<string, unknown> | null | undefined
) => {
  if (pageDTO.root.collection.length > 0 && 'collection' in pageDTO.root.collection[0]) {
    const validationValues = (pageDTO.root as SectionContainer).collection[0].collection.map((item) =>
      validateIndividualModule(item, rulesConfig)
    );

    const isValid = validationValues.flat().every((isValid) => typeof isValid === 'boolean');

    if (!isValid) {
      const invalidTypes = new Set(validationValues.flat().filter((isValid) => typeof isValid !== 'boolean'));

      return [...invalidTypes];
    }

    return isValid;
  }

  return false;
};

export const validateSoftLimit = (
  pageDTO: { root: Container; metadata: Metadata },
  groupConfigs?: Maybe<GroupConfig[]>
) => {
  const groupModuleCount = new Map<string, number>();

  (pageDTO.root as SectionContainer).collection[0].collection.forEach((item) => {
    const { groupId } = item;

    if (!groupId) {
      return;
    }

    if (!groupModuleCount.has(groupId)) {
      groupModuleCount.set(groupId, 0);
    }
    groupModuleCount.set(groupId, groupModuleCount.get(groupId)! + 1);
  });

  const validationErrors = [...groupModuleCount.entries()].map(([groupId, count]) => {
    const groupConfig = groupConfigs?.find((config) => config.id === groupId);
    if (groupConfig) {
      const { softLimit, softLimitMessage, label } = groupConfig;
      if (softLimit && count > softLimit) {
        const defaultErrorMessage = `The group ${label} has exceeded the soft limit of ${softLimit} modules. It should have less than ${softLimit} modules.`;
        return { hasError: true, errorMessage: softLimitMessage ?? defaultErrorMessage };
      }
    }

    return { hasError: false, errorMessage: '' };
  });

  return validationErrors.filter((error) => error.hasError);
};

export const MOBILE_HOMEPAGE_LAYOUT_ID = 'mobile-homepage';
export const MOBILE_FEED_GROUP_ID = 'MOBILE_FEED';
export const RIGHT_RAIL_TOP_OPINION_MAX_POSITION = 12;

const getModuleTreatmentType = (item: ModuleContainerUnion): string | undefined =>
  (item as MultiZoneModuleContainer | ModuleContainer).attributes.pageModule?.uiModuleFields?.titleModule?.treatmentType ?? undefined;

export const getMobileFeedOpinionPosition = (modules: ModuleContainerUnion[]): number | null => {
  const idx = modules.findIndex(
    (m) => getModuleTreatmentType(m) === UiTitleTreatmentType.RightRailTopOpinion
  );
  return idx === -1 ? null : idx + 1;
};

export const getMobileFeedDesktopTopperOpinionPosition = (modules: ModuleContainerUnion[]): number | null => {
  const idx = modules.findIndex(
    (m) => getModuleTreatmentType(m) === UiTitleTreatmentType.WsjDesktopTopperOpinion
  );
  return idx === -1 ? null : idx + 1;
};

export const validateHierarchyAboveRightRailTopOpinion = (
  pageDTO: { root: Container; metadata: Metadata }
): { hasError: boolean; errorMessage: string }[] => {
  const allModules = ((pageDTO.root as SectionContainer).collection[0] as SectionContainer['collection'][number] | undefined)?.collection;
  if (!allModules) return [];
  const feedModules = allModules.filter((m) => (m as ModuleContainer).groupId === MOBILE_FEED_GROUP_ID);

  // If WSJ_DESKTOP_TOPPER_OPINION is within the threshold, RIGHT_RAIL_TOP_OPINION validation is waived
  const desktopTopperPosition = getMobileFeedDesktopTopperOpinionPosition(feedModules);
  if (desktopTopperPosition !== null && desktopTopperPosition <= RIGHT_RAIL_TOP_OPINION_MAX_POSITION - 1) {
    return [];
  }

  const position = getMobileFeedOpinionPosition(feedModules);

  if (position === null) {
    return [{ hasError: true, errorMessage: 'The Opinion module is missing from the Mobile Feed.' }];
  }

  if (position > RIGHT_RAIL_TOP_OPINION_MAX_POSITION) {
    return [{
      hasError: true,
      errorMessage: `The Opinion module is at position ${position} in the Mobile Feed. Move it to position ${RIGHT_RAIL_TOP_OPINION_MAX_POSITION} or earlier.`,
    }];
  }

  return [];
};
